export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export const maxOutArray = <T = any, TLimit = any>(
  arr: Array<T>,
  max?: number,
  limitText?: (text: string) => TLimit
) => {
  if (!max || max >= arr.length) return arr;

  const text = `+${arr.length - max}`;
  const isLimitText = limitText ? limitText(text) : text;

  return [...arr.slice(0, max), isLimitText];
};

export const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

export const getValues = <T extends object>(obj: T | null | undefined) =>
  obj ? (Object.values(obj) as T[keyof T][]) : [];

export const getCommaSeparatedValues = <T extends object>(obj: T): string =>
  getValues(obj).map(String).join(', ');

export const getKeys = <T extends object>(obj: T | null | undefined) =>
  obj ? (Object.keys(obj) as (keyof T)[]) : [];

export const objectHasValues = (obj: Record<string, unknown>) => getValues(obj).length > 0;

// Helper function to safely merge arrays
export const safeMergeArrays = <T1, T2>(arr1: Array<T1> = [], arr2: Array<T2> = []) => [
  ...(Array.isArray(arr1) ? arr1 : []),
  ...(Array.isArray(arr2) ? arr2 : []),
];

export function mergeUniqueArray<T>(arr: Array<T>, arr2: Array<T> | T | null): Array<T> {
  // eslint-disable-next-line no-nested-ternary
  const arrValue = Array.isArray(arr2) ? arr2 : arr2 !== null ? [arr2] : [];

  return Array.from(new Set([...arr, ...arrValue]));
}

export function buildIdNameObject(ids: Array<number | string> = [], names: string[] = []) {
  return ids.reduce<Record<string, string>>((acc, id, index) => {
    const name = names[index];
    if (id == null || name == null) return acc; // In case the lists are different lengths
    // eslint-disable-next-line no-param-reassign
    acc[id.toString()] = name;
    return acc;
  }, {});
}

export function dedupeByField<T, K extends keyof T>(field: K, items: T[]): T[] {
  const arr = items ?? [];
  const seen = new Set<string>();

  return arr.filter((item) => {
    const value = item[field];
    const key = Array.isArray(value) ? JSON.stringify(value) : String(value);

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
